import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { AppPaths } from '@core/router/app-paths';
import { AuthTokenService } from '@core/services/auth-token.service';
import { SidebarModulesService } from '@core/services/sidebar-modules.service';
import { UserSessionService } from '@core/services/user-session.service';
import { SidebarAppRole, normalizeSidebarRole } from '@shared/ui/sidebar/sidebar-nav.util';
import { moduleRouteFromUrl } from '../navigation/sidebar-module-catalog';

/**
 * Bookmarked module URLs that the latest sidebar payload does not include
 * redirect to the first returned module. Routes this app does not map are left alone.
 */
export const sidebarModuleGuard: CanActivateFn = (_route, state) => {
  const tokens = inject(AuthTokenService);
  if (!tokens.isAuthenticated()) {
    return true;
  }

  const sidebar = inject(SidebarModulesService);
  const session = inject(UserSessionService);
  const router = inject(Router);
  const isSuperAdmin = normalizeSidebarRole(session.user()) === SidebarAppRole.SuperAdmin;

  const decide = (): boolean | ReturnType<Router['parseUrl']> => {
    const moduleRoute = moduleRouteFromUrl(state.url);

    // Module names is a Super Admin editor. The CRM sidebar payload does not list it.
    if (moduleRoute === AppPaths.moduleNames) {
      if (isSuperAdmin) {
        return true;
      }
      const next = sidebar.homeRoute() ?? AppPaths.dashboard;
      return next === AppPaths.moduleNames ? true : router.parseUrl(`/${next}`);
    }

    if (sidebar.status() === 'error' || sidebar.status() === 'idle') {
      return true;
    }

    if (!moduleRoute || sidebar.isRouteAllowed(moduleRoute)) {
      return true;
    }

    const next = sidebar.homeRoute();
    if (!next || next === moduleRoute) {
      return true;
    }

    return router.parseUrl(`/${next}`);
  };

  if (sidebar.status() === 'ready') {
    return decide();
  }

  return sidebar.ensureLoaded().pipe(map(() => decide()));
};
